import React, { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FormControl, Stack, TextField, Select, MenuItem, InputLabel, Button, Box, Chip, Typography
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useDiagnosticProblems, useDeleteDiagnosticProblem, useDiagnosticMainCategories, useDiagnosticSubCategories, useUpdateProblemApproval } from '@/hooks/diagnostic.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { DateCell } from '@/components/pages/components';
import { LANGUAGE_OPTIONS } from '@/constants/trainings';
import ProblemStatusCell from './../../components/ProblemStatusCell';
import { buildPageInfoParamsObject, parsePageInfoFromSearch } from './utils/pageInfoParams';
import TagsCell from './../../components/TagsCell';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { readListViewSnapshot, writeListViewSnapshot, clearListViewSnapshot } from '@/util/listViewSessionPersistence';

/** 조회 API에서 정렬 허용 컬럼: 문제 ID만 */
const DIAGNOSTIC_GRID_SORT_FIELD_MAP = {
  problemId: 'problemId',
};

const DIAGNOSTIC_SORT_FIELD_TO_GRID = Object.fromEntries(
  Object.entries(DIAGNOSTIC_GRID_SORT_FIELD_MAP).map(([k, v]) => [v, k])
);

function buildSortUrlParams(sortModel) {
  if (!sortModel?.length) return {};
  const { field, sort } = sortModel[0];
  const apiField = DIAGNOSTIC_GRID_SORT_FIELD_MAP[field];
  if (!apiField) return {};
  return { sortBy: apiField, sortOrder: sort };
}

function buildSortApiParams(sortModel) {
  if (!sortModel?.length) return {};
  const { field, sort } = sortModel[0];
  const apiField = DIAGNOSTIC_GRID_SORT_FIELD_MAP[field];
  if (!apiField) return {};
  return { sort: `${apiField},${sort}` };
}

/**
 * 진단 및 대응훈련 관리 페이지 컴포넌트
 * 
 * 관리자가 진단 및 대응훈련 문제를 출제, 확인, 검토, 수정할 수 있는 페이지입니다.
 * 
 * 주요 기능:
 * - 문제 목록 조회 (필터링: 카테고리, 난이도, 상태, 검색)
 * - 문제 출제 (등록)
 * - 문제 상세 확인
 * - 문제 검토 (상태 변경: 검토중, 승인, 반려)
 * - 문제 수정/삭제
 */
const TrainingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageInfoRestoredRef = useRef(false);
  const trainingsSessionRestoredRef = useRef(false);
  const [sortModel, setSortModel] = useState([]);

  // 언어 선택 상태 (초기값: null - 선택 전)
  const [languageId, setLanguageId] = useState(null);

  // 검색 및 필터 상태
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSubCategory, setFilterSubCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // URL에서 페이지 정보 파싱 (쿼리가 있을 때만 복원)
  const pageInfo = useMemo(() => {
    if (!searchParams.toString()) return null;
    const { pageInfo: parsed } = parsePageInfoFromSearch(searchParams);
    return parsed;
  }, [searchParams]);

  // 실제 쿼리용 상태
  const [queryOptions, setQueryOptions] = useState({
    pageSize: 10,
    page: 1,
    problemId: '',
    categoryId: null,
    subCategoryId: null,
    difficulty: null,
    isApproval: null,
  });

  // pageInfo가 있을 때 상태 복원 (한 번만 실행)
  useEffect(() => {
    if (pageInfo && !pageInfoRestoredRef.current) {
      pageInfoRestoredRef.current = true;
      
      // 언어 ID 복원 (먼저 설정해야 카테고리 데이터를 가져올 수 있음)
      if (pageInfo.languageId) {
        setLanguageId(pageInfo.languageId);
      }
      
      // 검색어 복원
      if (pageInfo.problemId !== undefined) {
        setSearchKeyword(pageInfo.problemId);
      }
      
      // 필터 상태 복원
      if (pageInfo.level) {
        setFilterDifficulty(pageInfo.level);
      }
      if (pageInfo.isApproval !== null && pageInfo.isApproval !== undefined) {
        setFilterStatus(pageInfo.isApproval === true ? 'true' : 'false');
      }
      if (pageInfo.categoryId) {
        setFilterCategory(pageInfo.categoryId.toString());
      }
      if (pageInfo.subCategoryId) {
        setFilterSubCategory(pageInfo.subCategoryId.toString());
      }
      
      // 쿼리 옵션 복원
      setQueryOptions({
        pageSize: pageInfo.size || 10,
        page: pageInfo.page || 1,
        problemId: pageInfo.problemId || '',
        categoryId: pageInfo.categoryId || null,
        subCategoryId: pageInfo.subCategoryId || null,
        difficulty: pageInfo.level || null,
        isApproval: pageInfo.isApproval !== null && pageInfo.isApproval !== undefined 
          ? pageInfo.isApproval 
          : null,
      });
      if (pageInfo.sortBy && pageInfo.sortOrder) {
        const gridField = DIAGNOSTIC_SORT_FIELD_TO_GRID[pageInfo.sortBy];
        if (gridField) {
          setSortModel([{ field: gridField, sort: pageInfo.sortOrder }]);
        }
      }
    }
  }, [pageInfo]);

  // 문제 목록 조회 (언어 선택 시 전체 목록, 문제 번호 검색 시 언어 없이도 조회)
  const { data, isLoading } = useDiagnosticProblems({
    page: queryOptions.page,
    size: queryOptions.pageSize,
    problemId: queryOptions.problemId,
    languageId: languageId,
    level: queryOptions.difficulty,
    isApproval: queryOptions.isApproval,
    categoryId: queryOptions.categoryId,
    subCategoryId: queryOptions.subCategoryId,
    ...buildSortApiParams(sortModel),
  }, {
    enabled: !!languageId || !!queryOptions.problemId,
  });

  const total = data?.total ?? 0;
  const rowData = data?.list ?? [];

  // rowCount가 로딩 중 0으로 바뀌면 DataGrid가 페이지를 0으로 리셋함. 로딩 중에는 이전 값 유지
  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) {
      setRowCountState(total);
    }
  }, [total, isLoading]);

  // 삭제 및 상태 변경 훅
  const deleteTrainingMutation = useDeleteDiagnosticProblem();
  const updateProblemApprovalMutation = useUpdateProblemApproval();

  // 카테고리 및 서브카테고리 조회 (보안약점유형 선택 시에만 서브카테고리 조회)
  const { data: categoriesData } = useDiagnosticMainCategories();
  const { data: subCategoriesData } = useDiagnosticSubCategories(
    filterCategory !== 'all' ? parseInt(filterCategory, 10) : undefined
  );

  const categories = categoriesData || [];
  const subCategories = subCategoriesData || [];

  // URL params 동기화 헬퍼
  const syncUrlParams = useCallback((pageInfo) => {
    const params = buildPageInfoParamsObject(pageInfo);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const buildSyncPageInfo = useCallback((overrides = {}) => {
    const sort = buildSortUrlParams(sortModel);
    return {
      page: queryOptions.page,
      size: queryOptions.pageSize,
      languageId,
      level: queryOptions.difficulty,
      isApproval: queryOptions.isApproval,
      categoryId: queryOptions.categoryId,
      subCategoryId: queryOptions.subCategoryId,
      problemId: queryOptions.problemId,
      ...sort,
      ...overrides,
    };
  }, [queryOptions, languageId, sortModel]);

  // sessionStorage에 목록 상태 저장 (상세 후 빈 URL로 복귀 시 복원)
  useEffect(() => {
    writeListViewSnapshot(LIST_VIEW_SESSION_KEYS.trainings, {
      filters: {
        languageId,
        searchKeyword,
        filterCategory,
        filterSubCategory,
        filterDifficulty,
        filterStatus,
        queryOptions,
        sortModel,
      },
    });
  }, [languageId, searchKeyword, filterCategory, filterSubCategory, filterDifficulty, filterStatus, queryOptions, sortModel]);

  // URL 쿼리가 없을 때 session에서 목록 상태 복원 (useLayoutEffect로 저장 effect보다 먼저 반영)
  useLayoutEffect(() => {
    if (searchParams.toString()) return;
    if (trainingsSessionRestoredRef.current) return;
    const blob = readListViewSnapshot(LIST_VIEW_SESSION_KEYS.trainings)?.filters;
    if (!blob || typeof blob !== 'object') return;
    trainingsSessionRestoredRef.current = true;
    pageInfoRestoredRef.current = true;

    if (blob.languageId != null && blob.languageId !== '') {
      setLanguageId(blob.languageId);
    }
    if (blob.searchKeyword !== undefined && blob.searchKeyword !== null) {
      setSearchKeyword(blob.searchKeyword);
    }
    if (blob.filterCategory != null) setFilterCategory(blob.filterCategory);
    if (blob.filterSubCategory != null) setFilterSubCategory(blob.filterSubCategory);
    if (blob.filterDifficulty != null) setFilterDifficulty(blob.filterDifficulty);
    if (blob.filterStatus != null) setFilterStatus(blob.filterStatus);
    if (blob.queryOptions && typeof blob.queryOptions === 'object') {
      setQueryOptions(blob.queryOptions);
    }
    if (Array.isArray(blob.sortModel)) {
      setSortModel(blob.sortModel);
    }
    const qo = blob.queryOptions || {};
    const sortParams = buildSortUrlParams(Array.isArray(blob.sortModel) ? blob.sortModel : []);
    syncUrlParams({
      page: qo.page ?? 1,
      size: qo.pageSize ?? 10,
      languageId: blob.languageId ?? null,
      level: qo.difficulty ?? null,
      isApproval: qo.isApproval !== undefined && qo.isApproval !== null ? qo.isApproval : null,
      categoryId: qo.categoryId ?? null,
      subCategoryId: qo.subCategoryId ?? null,
      problemId: qo.problemId ?? '',
      ...sortParams,
    });
  }, [searchParams, syncUrlParams]);

  // 검색 실행 핸들러
  const handleSearch = useCallback(() => {
    const newQueryOptions = {
      problemId: searchKeyword,
      categoryId: filterCategory !== 'all' ? parseInt(filterCategory) : null,
      subCategoryId: filterSubCategory !== 'all' ? parseInt(filterSubCategory) : null,
      difficulty: filterDifficulty !== 'all' ? filterDifficulty : null,
      isApproval: filterStatus !== 'all' ? (filterStatus === 'true' ? true : filterStatus === 'false' ? false : null) : null,
      pageSize: queryOptions.pageSize,
      page: 1,
    };
    setQueryOptions(newQueryOptions);
    syncUrlParams(buildSyncPageInfo({
      page: 1,
      size: newQueryOptions.pageSize,
      languageId,
      level: newQueryOptions.difficulty,
      isApproval: newQueryOptions.isApproval,
      categoryId: newQueryOptions.categoryId,
      subCategoryId: newQueryOptions.subCategoryId,
      problemId: newQueryOptions.problemId,
    }));
  }, [searchKeyword, filterCategory, filterSubCategory, filterDifficulty, filterStatus, queryOptions.pageSize, languageId, syncUrlParams, buildSyncPageInfo]);

  // 검색 초기화 핸들러 (languageIdOverride: 언어 변경 시 새 언어 ID)
  const handleReset = useCallback((languageIdOverride) => {
    const effectiveLanguageId = languageIdOverride !== undefined ? languageIdOverride : languageId;
    setSearchKeyword('');
    setFilterCategory('all');
    setFilterSubCategory('all');
    setFilterDifficulty('all');
    setFilterStatus('all');
    setSortModel([]);
    setQueryOptions({
      problemId: '',
      categoryId: null,
      subCategoryId: null,
      difficulty: null,
      isApproval: null,
      pageSize: 10,
      page: 1,
    });
    syncUrlParams({
      page: 1,
      size: 10,
      languageId: effectiveLanguageId,
      level: null,
      isApproval: null,
      categoryId: null,
      subCategoryId: null,
      problemId: '',
    });
    clearListViewSnapshot(LIST_VIEW_SESSION_KEYS.trainings);
  }, [languageId, syncUrlParams]);

  // 언어 변경 핸들러
  const handleLanguageChange = useCallback((event) => {
    const selectedLanguageId = event.target.value === '' ? null : parseInt(event.target.value);
    setLanguageId(selectedLanguageId);
    handleReset(selectedLanguageId);
  }, [handleReset]);

  // 문제 삭제 핸들러
  const handleDelete = useCallback((problemId) => {
    confirmToast(`"${problemId}번 문제"를 삭제하시겠습니까?\n삭제 후 복구가 불가능하기 때문에 가급적 상태를 반려로 변경하는 것을 권장합니다.`, () => {
      deleteTrainingMutation.mutate(problemId, {
        onSuccess: () => {
          successToast('문제가 삭제되었습니다.');
        },
        onError: () => {
          errorToast('문제 삭제에 실패했습니다.');
        },
      });
    });
  }, [deleteTrainingMutation]);

  // 행 클릭 핸들러 (상세 페이지로 이동)
  const handleRowClick = useCallback((params) => {
    navigate(`/education/trainings/detail/${params.row.problemId}`);
  }, [navigate, rowData, queryOptions, languageId]);

  // 셀 클릭 핸들러 (작업 버튼 셀에서 행 클릭 방지)
  const handleCellClick = useCallback((params, event) => {
    if (params.field === 'actions') {
      event.stopPropagation();
    }
  }, []);

  const handleSortModelChange = useCallback((newModel) => {
    setSortModel(newModel);
    setQueryOptions((prev) => ({ ...prev, page: 1 }));
    const sort = buildSortUrlParams(newModel);
    syncUrlParams({
      page: 1,
      size: queryOptions.pageSize,
      languageId,
      level: queryOptions.difficulty,
      isApproval: queryOptions.isApproval,
      categoryId: queryOptions.categoryId,
      subCategoryId: queryOptions.subCategoryId,
      problemId: queryOptions.problemId,
      ...sort,
    });
  }, [queryOptions, languageId, syncUrlParams]);

  // 문제 상태 변경 핸들러
  const handleProblemStatusChange = useCallback((problemId, isApproval) => {
    updateProblemApprovalMutation.mutate({ problemId, approval: !isApproval });
  }, [updateProblemApprovalMutation]);


  // 난이도 표시 헬퍼 함수
  const getDifficultyInfo = useCallback((difficulty) => {
    const difficultyMap = {
      EASY: { label: '쉬움', color: 'success' },
      NORMAL: { label: '보통', color: 'warning' },
      HARD: { label: '어려움', color: 'error' },
    };
    return difficultyMap[difficulty] || { label: difficulty, color: 'default' };
  }, []);

  // 컬럼 정의
  const columns = useMemo(() => [
    {
      field: 'problemId',
      headerName: '문제 ID',
      flex: 0.3,
      minWidth: 90,
      sortable: true,
    },
    {
      field: 'createdAt',
      headerName: '등록일',
      flex: 0.3,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="createdAt" />,
    },
    {
      field: 'category',
      headerName: '보안약점유형',
      flex: 0.6,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const categoryName = params.row.category?.name;
        if (!categoryName) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Chip
            label={categoryName}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'subCategory',
      headerName: '보안약점',
      flex: 0.8,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => {
        const subCategoryName = params.row.category?.subCategory?.name;
        const subCategoryId = params.row.category?.subCategory?.id;
        if (!subCategoryName) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Chip
            label={'[' + subCategoryId + '] '+ subCategoryName}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'level',
      headerName: '난이도',
      flex: 0.3,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => {
        const difficultyInfo = getDifficultyInfo(params.value);
        return (
          <Chip
            label={difficultyInfo.label}
            size="small"
            color={difficultyInfo.color}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'isApproval',
      headerName: '승인 여부',
      flex: 0.4,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const statusInfo = params.row.isApproval
          ? { color: 'success', label: '승인' } 
          : { color: 'error', label: '반려' };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ProblemStatusCell
              row={params.row}
              onStatusChange={handleProblemStatusChange}
            />
            <Chip
              label={statusInfo.label}
              size="small"
              color={statusInfo.color}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'tags',
      headerName: '태그',
      flex: 1,
      minWidth: 350,
      sortable: false,
      renderCell: (params) => {
        return <TagsCell row={params.row} />;
      },
    },
    {
      field: 'actions',
      headerName: '작업',
      flex: 1.8,
      minWidth: 280,
      sortable: false,
      renderCell: (params) => {
        return (
          <div style={{ display: 'inline-flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(params.row.problemId);
              }}
              >
              삭제
            </Button>
          </div>
        );
      },
    }
  ], [navigate, getDifficultyInfo, handleDelete, rowData, handleProblemStatusChange]);

  return (
    <Container title='진단 및 대응훈련 관리'>
      {/* 언어 선택 및 검색 안내 */}
      <Stack direction="row" alignItems="center" mb={3} gap={2}>
        <FormControl size='small' sx={{ width: 200 }}>
          <InputLabel>언어 선택</InputLabel>
          <Select
            value={languageId || ''}
            label="언어 선택"
            onChange={handleLanguageChange}
          >
            <MenuItem value="">전체</MenuItem>
            {LANGUAGE_OPTIONS.map((lang) => (
              <MenuItem key={lang.languageId} value={lang.languageId}>
                {lang.languageName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!languageId && (
          <Typography variant="body2" color="text.secondary">
            언어를 선택하면 문제 목록을 조회할 수 있습니다. 또는 문제 번호로 검색할 수 있습니다.
          </Typography>
        )}
      </Stack>

      {/* 상단 필터 및 버튼 영역 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        {/* 검색 필터 그룹 */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size='small'
            placeholder='문제 ID 검색'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />

          <FormControl size='small' sx={{ width: 150 }}>
            <InputLabel>보안약점유형</InputLabel>
            <Select
              value={filterCategory}
              label="보안약점유형"
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setFilterSubCategory('all'); // 카테고리 변경 시 서브카테고리 초기화
              }}
              disabled={!languageId}
            >
              <MenuItem value='all'>전체</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.mainCategoryId} value={category.mainCategoryId}>
                  {category.mainCategoryId*100+'번'} {category.mainCategoryName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 150 }}>
            <InputLabel>보안약점</InputLabel>
            <Select
              value={filterSubCategory}
              label="보안약점"
              onChange={(e) => setFilterSubCategory(e.target.value)}
              disabled={!languageId || filterCategory === 'all'}
            >
              <MenuItem value='all'>전체</MenuItem>
              {subCategories.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  {sub.id}: {sub.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 120 }}>
            <InputLabel>난이도</InputLabel>
            <Select
              value={filterDifficulty}
              label="난이도"
              onChange={(e) => setFilterDifficulty(e.target.value)}
              disabled={!languageId}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='EASY'>쉬움</MenuItem>
              <MenuItem value='NORMAL'>보통</MenuItem>
              <MenuItem value='HARD'>어려움</MenuItem>
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ width: 120 }}>
            <InputLabel>상태</InputLabel>
            <Select
              value={filterStatus}
              label="상태"
              onChange={(e) => setFilterStatus(e.target.value)}
              disabled={!languageId}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='true'>승인됨</MenuItem>
              <MenuItem value='false'>반려됨</MenuItem>
            </Select>
          </FormControl>

          <Button 
            variant='contained' 
            onClick={handleSearch} 
            disabled={!languageId && !searchKeyword}
            sx={{ height: 40 }}
          >
            검색
          </Button>
          <Button 
            variant='outlined' 
            onClick={handleReset}
            disabled={!languageId && !searchKeyword && filterCategory === 'all' && filterSubCategory === 'all' && filterDifficulty === 'all' && filterStatus === 'all'}
            sx={{ height: 40 }}
          >
            초기화
          </Button>
        </Stack>

        {/* 우측 액션 버튼 그룹 */}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/education/trainings/new`)}
          >
            문제 출제
          </Button>
        </Stack>
      </Stack>

      {/* 데이터 그리드 */}
      {!languageId && !queryOptions.problemId ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            border: '1px dashed #ccc',
            borderRadius: 1,
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="h6" color="text.secondary">
            언어를 선택하면 문제 목록을 확인할 수 있습니다. 또는 문제 번호로 검색할 수 있습니다.
          </Typography>
        </Box>
      ) : (
        <DataGrid
          rows={rowData}
          columns={columns}
          loading={isLoading}
          rowCount={rowCountState}
          height={'auto'}
          paginationModel={{
            page: queryOptions.page - 1, // API는 1부터 시작하지만 DataGrid는 0부터 시작
            pageSize: queryOptions.pageSize,
          }}
          onPaginationModelChange={(newModel) => {
            const newPage = newModel.page + 1;
            const newPageSize = newModel.pageSize;
            setQueryOptions((prev) => ({
              ...prev,
              page: newPage,
              pageSize: newPageSize,
            }));
            syncUrlParams(buildSyncPageInfo({
              page: newPage,
              size: newPageSize,
            }));
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          getRowId={(row) => row.problemId}
          onRowClick={handleRowClick}
          onCellClick={handleCellClick}
        />
      )}
    </Container>
  );
};

export default TrainingsPage;
